import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import useApi from "../../api/apiConfig";
import useQueryClient from "../../api/query";
import { PREDEFINED_CLAIM_FILTERS, REVIEW_VIEW } from "../../status";
import {
  type BackendFilters,
  createDefaultBackendFilters,
  type DataGridFilter,
  FilterLogicalOperator,
  FilterOperator,
  validateBackendFilters,
} from "../../utils/backendFilters";
import { useConfig } from "../../utils/config";
import useDevMode from "../../utils/devmode";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "../../utils/featureFlags";
import { boostReplay } from "../../utils/posthog";
import {
  camelToSnakeCase,
  getTodayISODate,
  practiceSelectionKey,
} from "../../utils/utils";
import { Action } from "../actions";
import {
  deleteClaimAndNotify,
  deleteProcedureAndNotify,
  saveProcedureAndNotify,
} from "../crud";
import { useClaimsPostingPoll } from "../polling";
import { postPMSUpdateAndNotify } from "../posting";
import { validateClaimAndNotify } from "../validation";
import { getInsuranceArEntryForClaim } from "./api";
import BalanceAdjustmentFollowUpDialog from "./BalanceAdjustmentFollowUpDialog";
import ClaimsListView from "./ClaimsListView";
import {
  appendClaimRow,
  applyClaimsAction,
  bumpClaimsTotal,
  CLAIMS_KEY,
  type ClaimsCacheAction,
  patchClaim,
  prependClaimRow,
  removeClaimRow,
  useBatchPostToPMS,
  useClaims,
  useCreateClaim,
  useInsuranceArActionOptions,
  useLaterClaims,
  useUpdateClaim,
  useUpdateDenial,
  useUpdateInsuranceArEntry,
  useUpdateStringifyClaim,
} from "./hooks";

interface ClaimsDashboardProps {
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  selectedPractices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
}

interface FilterItem {
  field: string;
  operator: string;
  value: unknown;
}

const DEFAULT_PAGE_SIZE = 10;
const DENISE_SPLIT_PAGE_SIZE = 5;
const NO_CLAIMS: ClaimWithProcedureAndPatientMessage[] = [];

const isDueNow = (
  claim: ClaimWithProcedureAndPatientMessage,
  dueNowDaysThreshold: number,
): boolean =>
  (claim.daysSinceNotice ?? 0) >= dueNowDaysThreshold ||
  !claim.denialUrgency ||
  claim.denialUrgency === "HIGH";

const mapFilterToTab = (filter: PREDEFINED_CLAIM_FILTERS): string => {
  switch (filter) {
    case PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS:
      return "ALL_CLAIMS";
    case PREDEFINED_CLAIM_FILTERS.REVIEW:
      return "REVIEW";
    case PREDEFINED_CLAIM_FILTERS.DENIALS:
      return "DENIALS";
    default:
      return "ALL_CLAIMS";
  }
};

export default function ClaimsDashboard({
  updateSnackBar,
  selectedPractices,
  supportedPayers,
  supportedSources,
}: ClaimsDashboardProps) {
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const [isBatchPosting, updateIsBatchPosting] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const recentlyRemovedRef = useRef<Set<string>>(new Set());
  const filterRecentlyRemoved = (
    data: ClaimWithProcedureAndPatientMessage[],
  ): ClaimWithProcedureAndPatientMessage[] => {
    const set = recentlyRemovedRef.current;
    if (set.size === 0) return data;
    const serverKeys = new Set(data.map((c) => c.wieldyId));
    set.forEach((key) => {
      if (!serverKeys.has(key)) set.delete(key);
    });
    return data.filter((c) => !set.has(c.wieldyId));
  };
  const location = useLocation();
  const [selectedFilter, setSelectedFilter] =
    React.useState<PREDEFINED_CLAIM_FILTERS>(
      PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS,
    );
  const [activeView, setActiveView] = useState<REVIEW_VIEW | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const { isEnabled: isDeniseEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.DENISE,
  );
  const { isEnabled: insuranceArEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.INSURANCE_AR,
  );
  const { config } = useConfig();
  const isSplitDeniseView =
    isDeniseEnabled &&
    selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW &&
    activeView === REVIEW_VIEW.DENISE;
  const [laterPagination, setLaterPagination] = useState({
    page: 1,
    pageSize: DENISE_SPLIT_PAGE_SIZE,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimKeyParam = searchParams.get("claim_key");
  const [arFollowUp, setArFollowUp] = useState<{
    entry: InsuranceArEntryMessage;
    practiceId: string;
    patientName: string;
    confirmedAction: string;
  } | null>(null);
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  const rqClient = useTanStackQueryClient();
  const isDevMode = useDevMode();
  const { data: arActionOptions } =
    useInsuranceArActionOptions(insuranceArEnabled);
  const updateClaimMutation = useUpdateClaim();
  const updateDenialMutation = useUpdateDenial();
  const createClaimMutation = useCreateClaim();
  const updateStringifyClaimMutation = useUpdateStringifyClaim();
  const batchPostMutation = useBatchPostToPMS();
  const updateInsuranceArEntryMutation = useUpdateInsuranceArEntry();

  // DENIALS filter is component state the URL can't see; route boosts live in replayBoosts.ts.
  useEffect(() => {
    if (selectedFilter === PREDEFINED_CLAIM_FILTERS.DENIALS) {
      boostReplay("denial_management");
    }
  }, [selectedFilter]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding activeView, searchParams.get, searchParams and 2 more would change when this runs
  useEffect(() => {
    const isReview = selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW;
    if (isDeniseEnabled && isReview && activeView === null) {
      const urlSubFilter = searchParams.get("view");
      const validUrlValue =
        urlSubFilter &&
        Object.values(REVIEW_VIEW).includes(urlSubFilter as REVIEW_VIEW)
          ? (urlSubFilter as REVIEW_VIEW)
          : null;

      const resolved = validUrlValue ?? REVIEW_VIEW.DENISE;
      setActiveView(resolved);

      if (searchParams.get("view") !== resolved) {
        const params = new URLSearchParams(searchParams);
        params.set("view", resolved);
        navigate({ search: params.toString() }, { replace: true });
      }
    }
    if (!isReview && activeView !== null) {
      setActiveView(null);
      if (searchParams.has("view")) {
        const params = new URLSearchParams(searchParams);
        params.delete("view");
        navigate({ search: params.toString() }, { replace: true });
      }
    }
  }, [isDeniseEnabled, selectedFilter]);

  const formatFilterValue = (
    operator: string,
    value: unknown,
  ): string | string[] => {
    if (["isEmpty", "isNotEmpty"].includes(operator)) return "";

    if (operator === "isAnyOf" && Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => String(v).trim());
    }

    return String(value || "");
  };

  const extractFiltersFromLocalStorage = (): BackendFilters => {
    try {
      const persistedState = localStorage.getItem("claimsListView");
      if (!persistedState) {
        return createDefaultBackendFilters();
      }

      const parsedState = JSON.parse(persistedState);
      const filterItems = parsedState?.filter?.filterModel?.items || [];
      const datagridFilterItems: Array<DataGridFilter> = [];

      filterItems
        .filter(
          (item: FilterItem) => item.field && typeof item.operator === "string",
        )
        .forEach((item: FilterItem) => {
          const backendField = camelToSnakeCase(item.field);

          datagridFilterItems.push({
            field: backendField,
            operator: item.operator as FilterOperator,
            value: formatFilterValue(item.operator, item.value),
          });
        });

      const result: BackendFilters = {
        filters: {},
        datagridFilters: datagridFilterItems,
        logicalOperator: FilterLogicalOperator.AND,
      };

      const errors = validateBackendFilters(result);
      if (errors.length > 0) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.warn("Filter validation errors:", errors);
        return createDefaultBackendFilters();
      }

      return result;
    } catch (_error) {
      return createDefaultBackendFilters();
    }
  };

  const [backendFilters, setBackendFilters] = useState<BackendFilters>(
    extractFiltersFromLocalStorage,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedPracticesKey stands in for selectedPractices
  const currentFilters = useMemo((): BackendFilters => {
    const result: BackendFilters = createDefaultBackendFilters();

    if (backendFilters.filters) {
      result.filters = { ...backendFilters.filters };
    }
    if (backendFilters.datagridFilters) {
      result.datagridFilters = [...backendFilters.datagridFilters];
    }
    if (backendFilters.logicalOperator) {
      result.logicalOperator = backendFilters.logicalOperator;
    }

    result.filters = {
      ...result.filters,
      practice_id:
        selectedPractices.length > 0
          ? selectedPractices.map((p) => p.wieldyId)
          : [],
    };

    if (claimKeyParam) {
      result.datagridFilters = [
        ...(result.datagridFilters ?? []),
        {
          field: "wieldy_id",
          operator: FilterOperator.EQUALS,
          value: claimKeyParam,
        },
      ];
    }

    return result;
  }, [backendFilters, selectedPracticesKey, claimKeyParam]);

  const updateStoreWithLocationState = (
    responseClaims: ClaimWithProcedureAndPatientMessage[],
  ) => {
    const state = location.state as {
      updatedClaims: ClaimWithProcedureAndPatientMessage[];
    } | null;

    if (!state?.updatedClaims) {
      return responseClaims;
    }

    return responseClaims.map((claim) => {
      const updatedClaim = state.updatedClaims.find(
        (c) => c.wieldyId === claim.wieldyId,
      );
      if (updatedClaim) {
        return updatedClaim;
      }
      return claim;
    });
  };

  const tab = mapFilterToTab(selectedFilter);
  const shouldSkipDefaultSort =
    isDeniseEnabled && selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW;
  const effectiveSort = sortField ?? (shouldSkipDefaultSort ? null : undefined);

  const claimsParams = useMemo(
    () => ({
      filters: currentFilters,
      tab,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sortField: effectiveSort,
      sortDirection,
      view: activeView,
      deniseQueue: isSplitDeniseView ? "due_now" : null,
      devMode: isDevMode,
    }),
    [
      currentFilters,
      tab,
      pagination.page,
      pagination.pageSize,
      effectiveSort,
      sortDirection,
      activeView,
      isSplitDeniseView,
      isDevMode,
    ],
  );
  const laterParams = useMemo(
    () => ({
      filters: currentFilters,
      tab,
      page: laterPagination.page,
      pageSize: laterPagination.pageSize,
      sortField,
      sortDirection,
      view: activeView,
      deniseQueue: "later",
      devMode: isDevMode,
    }),
    [
      currentFilters,
      tab,
      laterPagination.page,
      laterPagination.pageSize,
      sortField,
      sortDirection,
      activeView,
      isDevMode,
    ],
  );

  const claimsQuery = useClaims(claimsParams, true, (response) => ({
    ...response,
    data: updateStoreWithLocationState(filterRecentlyRemoved(response.data)),
  }));
  const laterQuery = useLaterClaims(
    laterParams,
    isSplitDeniseView,
    (response) => ({
      ...response,
      data: filterRecentlyRemoved(response.data),
    }),
  );
  const claims = claimsQuery.data?.data ?? NO_CLAIMS;
  const laterClaims = laterQuery.data?.data ?? NO_CLAIMS;

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar would re-fire the snackbar
  useEffect(() => {
    if (claimsQuery.isError) {
      updateSnackBar({ severity: "error", message: "Error loading claims." });
    }
  }, [claimsQuery.isError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar would re-fire the snackbar
  useEffect(() => {
    if (laterQuery.isError) {
      updateSnackBar({ severity: "error", message: "Error loading claims." });
    }
  }, [laterQuery.isError]);

  const updateStore = (action: ClaimsCacheAction) =>
    applyClaimsAction(rqClient, action);

  useClaimsPostingPoll(claims, queryClient, updateStore, updateSnackBar);

  // Reset paging during render so the query never fires with the old page.
  const resetKey = [
    selectedFilter,
    activeView,
    isSplitDeniseView,
    claimKeyParam,
    selectedPracticesKey,
  ].join("|");
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setPagination({
      page: 1,
      pageSize: isSplitDeniseView ? DENISE_SPLIT_PAGE_SIZE : DEFAULT_PAGE_SIZE,
    });
    setLaterPagination((prev) => ({ ...prev, page: 1 }));
  }

  const handleBackendFilterChange = (filters: BackendFilters) => {
    setBackendFilters(filters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setLaterPagination((prev) => ({ ...prev, page: 1 }));
  };

  const postToPMS = async (claim: ClaimWithProcedureAndPatientMessage) => {
    await postPMSUpdateAndNotify(
      claim,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  const updateClaim = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      await updateClaimMutation.mutateAsync({ claim, claimWork });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onDone = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      await updateDenialMutation.mutateAsync({
        claimWork,
        optimisticClaim: { ...claim, ...claimWork },
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const applyOptimisticDenialUpdate = (
    updated: ClaimWithProcedureAndPatientMessage,
  ) => {
    if (!isSplitDeniseView) {
      patchClaim(rqClient, updated);
      return;
    }
    const isInDueNow = claims.some((c) => c.wieldyId === updated.wieldyId);
    if (isDueNow(updated, config.deniseDueNowDaysThreshold)) {
      if (isInDueNow) {
        patchClaim(rqClient, updated);
      } else {
        removeClaimRow(rqClient, "later", updated.wieldyId);
        prependClaimRow(
          rqClient,
          "list",
          claimsParams,
          updated,
          pagination.pageSize,
        );
        bumpClaimsTotal(rqClient, "later", laterParams, -1);
        bumpClaimsTotal(rqClient, "list", claimsParams, 1);
      }
    } else if (isInDueNow) {
      removeClaimRow(rqClient, "list", updated.wieldyId);
      prependClaimRow(
        rqClient,
        "later",
        laterParams,
        updated,
        laterPagination.pageSize,
      );
      bumpClaimsTotal(rqClient, "list", claimsParams, -1);
      bumpClaimsTotal(rqClient, "later", laterParams, 1);
    } else {
      patchClaim(rqClient, updated);
    }
  };

  const maybeOpenBalanceFollowUp = async (
    claim: ClaimWithProcedureAndPatientMessage,
    confirmedAction: string,
  ) => {
    if (!insuranceArEnabled) return;
    try {
      const entry = await getInsuranceArEntryForClaim(
        await getClient(),
        claim.wieldyId,
        claim.practiceId,
      );
      if (entry && entry.status === "NEEDS_WORK") {
        setArFollowUp({
          entry,
          practiceId: claim.practiceId,
          patientName:
            claim.wieldyPatientName ?? claim.patientFullName ?? "This patient",
          confirmedAction,
        });
      }
    } catch (_error) {
      // No A/R linkage should never block the denial confirmation flow.
    }
  };

  const onArFollowUpConfirm = async (action: string) => {
    if (!arFollowUp) return;
    try {
      await updateInsuranceArEntryMutation.mutateAsync({
        entryId: arFollowUp.entry.id,
        practiceId: arFollowUp.practiceId,
        actionType: "BALANCE_ADJUSTMENT",
        action,
        confirmAction: true,
      });
      updateSnackBar({
        severity: "success",
        message: "Balance adjustment action recorded.",
      });
      setArFollowUp(null);
    } catch (_error) {
      updateSnackBar({
        severity: "error",
        message: "Failed to record the balance adjustment action.",
      });
    }
  };

  const onDenialUpdate = async (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => {
    try {
      const isSelection =
        denialWork.confirmAction !== true &&
        denialWork.userAction !== undefined;
      const updated: ClaimWithProcedureAndPatientMessage = {
        ...claim,
        ...(denialWork.userAction !== undefined && {
          userAction: denialWork.userAction,
        }),
        ...(denialWork.urgency !== undefined && {
          denialUrgency: denialWork.urgency,
        }),
        ...(isSelection && { actionSelectedAt: new Date().toISOString() }),
      };
      const isConfirmedTransition =
        denialWork.confirmAction === true &&
        denialWork.userAction != null &&
        denialWork.userAction !== Action.CALL_PAYER;
      if (isConfirmedTransition) {
        const wasInDueNow = claims.some((c) => c.wieldyId === claim.wieldyId);
        removeClaimRow(rqClient, "list", claim.wieldyId);
        removeClaimRow(rqClient, "later", claim.wieldyId);
        bumpClaimsTotal(
          rqClient,
          wasInDueNow ? "list" : "later",
          wasInDueNow ? claimsParams : laterParams,
          -1,
        );
        recentlyRemovedRef.current.add(claim.wieldyId);
      } else {
        applyOptimisticDenialUpdate(updated);
      }
      await updateDenialMutation.mutateAsync({
        claimWork: { wieldyId: claim.wieldyId, practiceId: claim.practiceId },
        denialWork,
      });
      if (isConfirmedTransition) {
        await rqClient.invalidateQueries({ queryKey: CLAIMS_KEY });
      }
      if (
        denialWork.confirmAction === true &&
        (denialWork.userAction === Action.PATIENT_RESPONSIBILITY ||
          denialWork.userAction === Action.WRITE_OFF)
      ) {
        await maybeOpenBalanceFollowUp(claim, denialWork.userAction);
      }
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message:
          error instanceof AxiosError && error.response?.data?.detail
            ? String(error.response.data.detail)
            : "Error updating denial.",
      });
    }
  };

  const onSaveProcedure = async (procedure: ProcedureMessage) => {
    await saveProcedureAndNotify(
      procedure,
      claims,
      queryClient,
      updateStore,
      updateSnackBar,
    );
  };

  const onSaveClaim = async (claim: ClaimMessage) => {
    try {
      if (claim.wieldyId) {
        const existingClaim = claims.find((c) => c.wieldyId === claim.wieldyId);
        if (!existingClaim) {
          return;
        }
        const deltas = (
          Object.entries(claim) as [keyof ClaimMessage, unknown][]
        ).reduce<Partial<ClaimMessage>>(
          (acc, [key, value]) => {
            const existingValue =
              existingClaim[key] && key === "payer"
                ? (existingClaim[key] as string).toUpperCase()
                : existingClaim[key];
            if (value !== undefined && existingValue !== value) {
              // Updates on the backend currently only accept string
              acc[key] = value;
            }
            return acc;
          },
          {} as Partial<ClaimMessage>,
        );
        if (Object.keys(deltas).length === 0) {
          updateSnackBar({
            severity: "warning",
            message: `Nothing to update`,
          });
          return;
        }
        const wieldyId = await updateStringifyClaimMutation.mutateAsync({
          update: {
            ...deltas,
            wieldyId: claim.wieldyId,
            practiceId: claim.practiceId,
          },
          patch: { ...existingClaim, ...claim },
        });
        updateSnackBar({
          severity: "success",
          message: `Successfully edited claim: ${wieldyId}`,
        });
        return;
      }

      const wieldyId = await createClaimMutation.mutateAsync(claim);
      const practice = selectedPractices.find(
        (p) => p.wieldyId === claim.practiceId,
      );
      const createdClaim: ClaimWithProcedureAndPatientMessage = {
        ...(claim as ClaimMessage),
        wieldyId,
        availableSince: getTodayISODate(),
        practice: practice?.name || "",
        practiceDisplayName: practice?.displayName || "",
        Patient: [],
        Procedure: [],
      };
      appendClaimRow(rqClient, claimsParams, createdClaim);
      updateSnackBar({
        severity: "success",
        message: `Successfully created claim: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const batchPostPMS = async () => {
    try {
      updateIsBatchPosting(true);
      updateSnackBar({
        severity: "info",
        message: "Started batching posting to PMS",
      });
      await batchPostMutation.mutateAsync();
      updateSnackBar({
        severity: "success",
        message: "Successfully started batch posting to PMS",
      });
      updateIsBatchPosting(false);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        updateSnackBar({
          severity: "error",
          message: "Failed to start batch posting to PMS",
        });
      }
      updateIsBatchPosting(false);
    }
  };

  const practiceSet = new Set(
    selectedPractices.map((practice) => practice.wieldyId),
  );

  const onPageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  const onSortChange = (field: string | null, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setLaterPagination((prev) => ({ ...prev, page: 1 }));
  };

  const onLaterPageChange = (newPage: number) => {
    setLaterPagination((prev) => ({ ...prev, page: newPage }));
  };

  const onLaterPageSizeChange = (newPageSize: number) => {
    setLaterPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  const validateClaim = async (claim: ClaimWithProcedureAndPatientMessage) => {
    await validateClaimAndNotify(
      claim,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  return (
    <>
      <BalanceAdjustmentFollowUpDialog
        entry={arFollowUp?.entry ?? null}
        confirmedAction={arFollowUp?.confirmedAction ?? ""}
        patientName={arFollowUp?.patientName ?? ""}
        balanceAdjustmentActions={
          arActionOptions?.balanceAdjustmentActions ?? []
        }
        onClose={() => setArFollowUp(null)}
        onConfirm={onArFollowUpConfirm}
      />
      <Routes>
        <Route
          path="/"
          element={
            <ClaimsListView
              claims={claims.filter((c) => practiceSet.has(c.practiceId))}
              loading={claimsQuery.isPending || claimsQuery.isPlaceholderData}
              batchPostPMS={batchPostPMS}
              isBatchPosting={isBatchPosting}
              updateClaim={updateClaim}
              postToPMS={postToPMS}
              selectedPractices={selectedPractices}
              onSaveClaim={onSaveClaim}
              onDeleteClaim={async (claim) => {
                await deleteClaimAndNotify(
                  claim,
                  queryClient,
                  updateStore,
                  updateSnackBar,
                );
              }}
              onSaveProcedure={onSaveProcedure}
              onDeleteProcedure={async (procedure) => {
                await deleteProcedureAndNotify(
                  procedure,
                  claims,
                  queryClient,
                  updateStore,
                  updateSnackBar,
                );
              }}
              supportedPayers={supportedPayers}
              supportedSources={supportedSources}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              activeView={activeView}
              onViewChange={setActiveView}
              onFilterChange={handleBackendFilterChange}
              pagination={{
                ...pagination,
                rowCount: claimsQuery.data?.total ?? 0,
                totalPages: claimsQuery.data?.total_pages ?? 1,
                onPageChange,
                onPageSizeChange,
              }}
              onSortChange={onSortChange}
              currentFilters={currentFilters}
              validateClaim={validateClaim}
              onDone={onDone}
              onDenialUpdate={onDenialUpdate}
              laterClaims={laterClaims.filter((c) =>
                practiceSet.has(c.practiceId),
              )}
              laterLoading={
                isSplitDeniseView &&
                (laterQuery.isPending || laterQuery.isPlaceholderData)
              }
              laterPagination={{
                ...laterPagination,
                rowCount: laterQuery.data?.total ?? 0,
                totalPages: laterQuery.data?.total_pages ?? 1,
                onPageChange: onLaterPageChange,
                onPageSizeChange: onLaterPageSizeChange,
              }}
            />
          }
        />
      </Routes>
    </>
  );
}
